// Procedural cyberpunk city: buildings, ground plane, neon lighting.
// All coordinates use positive Z — blink-controls known bug: teleporting to Z < 0
// breaks locomotion (https://github.com/jure/aframe-blink-controls/issues/30).

// Desktop click-to-teleport. Applied to the city entity automatically from city-builder init.
// When the mouse cursor clicks a [data-ground] surface, the rig moves to that point.
AFRAME.registerComponent('ground-teleport', {
  init: function () {
    this._onClick = this._onClick.bind(this);
    this.el.addEventListener('click', this._onClick);
  },
  _onClick: function (evt) {
    var cursorEl = (evt.detail && evt.detail.cursorEl) || document.querySelector('#mouse-cursor');
    if (!cursorEl) { return; }
    var raycaster = cursorEl.components && cursorEl.components.raycaster;
    if (!raycaster || !raycaster.intersections || !raycaster.intersections.length) { return; }
    var point = raycaster.intersections[0].point;
    var rig = document.querySelector('#rig');
    if (!rig) { return; }
    rig.setAttribute('position', { x: point.x, y: 0, z: point.z });
  },
  remove: function () {
    this.el.removeEventListener('click', this._onClick);
  },
});

// Building layout data. x/z = center, w = width (X), d = depth (Z), h = height.
// All Z positive. Heights 10–20% taller than real-world scale to counteract VR size-compression.
const BUILDINGS = [
  { x: -12, z:  5, w: 7, d: 5, h: 14 },  // front-left block, tall
  { x:  -5, z:  4, w: 4, d: 4, h: 10 },  // front-left inner
  { x:   8, z:  5, w: 5, d: 5, h: 18 },  // front-right block, very tall
  { x:  14, z:  4, w: 4, d: 4, h:  8 },  // front-right outer, short
  { x: -11, z: 17, w: 6, d: 6, h: 16 },  // mid-left
  { x:  -4, z: 19, w: 4, d: 5, h: 12 },  // mid-left inner
  { x:   7, z: 21, w: 5, d: 5, h: 14 },  // mid-right
  { x:  13, z: 18, w: 4, d: 4, h:  9 },  // mid-right outer
  { x:  -8, z: 27, w: 5, d: 4, h: 20 },  // back-left, tallest
  { x:   6, z: 28, w: 6, d: 5, h: 15 },  // back-right
];

AFRAME.registerComponent('city-builder', {
  init: function () {
    const { THREE } = AFRAME;

    // data-ground attribute must be on the A-Frame entity for blink-controls
    // collisionEntities: [data-ground] selector to work.
    this.el.setAttribute('data-ground', '');

    // Enable desktop click-to-teleport on this entity.
    this.el.setAttribute('ground-teleport', '');

    this._buildQueue = [...BUILDINGS];
    this._built = 0;
    this._group = new THREE.Group();

    // Shared material for all buildings — keeps all buildings in 1 draw call.
    this._buildingMat = new THREE.MeshLambertMaterial({ color: '#1a1a2e' });

    // Ground and grid are cheap (2 meshes) — create synchronously.
    this._createGround();
    this._createGrid();
    // Lighting must NOT go inside this._group — lights inside a THREE.Group are
    // invisible to A-Frame's internal light tracking. Use setObject3D instead.
    this._createLighting();

    this.el.setObject3D('city', this._group);

    // Disable fog evaluation on the sky mesh — sky color matches fog so no blending needed.
    this.el.sceneEl.addEventListener('loaded', () => {
      const sky = document.querySelector('a-sky');
      if (sky) {
        const skyMesh = sky.getObject3D('mesh');
        if (skyMesh) { skyMesh.material.fog = false; }
      }
    });
  },

  _createGround: function () {
    const { THREE } = AFRAME;
    // PlaneGeometry centered at z=15 (player spawn midpoint) to cover z: -25..55.
    // MeshLambertMaterial (not MeshStandardMaterial) — ground is the largest surface;
    // Lambert eliminates the full PBR BRDF cost on this surface.
    const groundGeo = new THREE.PlaneGeometry(80, 80);
    const groundMat = new THREE.MeshLambertMaterial({ color: '#060610' });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, 0, 15);
    this._group.add(ground);
  },

  _createGrid: function () {
    const { THREE } = AFRAME;
    // Grid covers the active city area: x: -20..20, z: 0..30. All Z positive.
    const xMin = -20, xMax = 20, zMin = 0, zMax = 30;
    const xSteps = 20, zSteps = 15;

    const positions = [];
    const colors = [];
    const colA = new THREE.Color('#00ddff');
    const colB = new THREE.Color('#ff00aa');
    const col = new THREE.Color();

    // Lines parallel to Z axis (x = constant)
    for (let i = 0; i <= xSteps; i++) {
      const t = i / xSteps;
      col.lerpColors(colA, colB, t);
      const x = xMin + (xMax - xMin) * t;
      positions.push(x, 0, zMin,  x, 0, zMax);
      colors.push(col.r, col.g, col.b,  col.r, col.g, col.b);
    }

    // Lines parallel to X axis (z = constant)
    for (let i = 0; i <= zSteps; i++) {
      const t = i / zSteps;
      col.lerpColors(colA, colB, t);
      const z = zMin + (zMax - zMin) * t;
      positions.push(xMin, 0, z,  xMax, 0, z);
      colors.push(col.r, col.g, col.b,  col.r, col.g, col.b);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(colors),    3));

    const mat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,      // prevent z-fighting write with the coplanar ground plane
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const lines = new THREE.LineSegments(geo, mat);
    lines.position.y = 0.005; // tiny lift above ground to avoid z-fighting
    this._group.add(lines);
  },

  _createLighting: function () {
    const { THREE } = AFRAME;

    // HemisphereLight: per-vertex computation, essentially free on Quest.
    // Sky/ground colors are dim blue/purple — enough to see building silhouettes
    // without washing out the neon PointLight glow.
    const hemi = new THREE.HemisphereLight(0x223366, 0x110022, 1.8);
    this.el.setObject3D('hemi-light', hemi);

    // PointLights: max 2, distance ≤ 12m, decay: 2 (inverse-square), castShadow: false.
    // Rule: lights MUST be added via setObject3D — NOT inside the group. Three.js objects
    // inside a Group are invisible to A-Frame's internal light tracking system.
    const neon1 = new THREE.PointLight(0xff00cc, 4, 12, 2);
    neon1.position.set(-8, 3, 8);
    this.el.setObject3D('light-0', neon1);

    const neon2 = new THREE.PointLight(0x00ccff, 3, 10, 2);
    neon2.position.set(6, 3, 12);
    this.el.setObject3D('light-1', neon2);
  },

  _spawnBuilding: function (b) {
    const { THREE } = AFRAME;
    const geo = new THREE.BoxGeometry(b.w, b.h, b.d);
    const mesh = new THREE.Mesh(geo, this._buildingMat);
    // Center building at (b.x, b.h/2, b.z) so its bottom sits at y=0.
    mesh.position.set(b.x, b.h / 2, b.z);
    this._group.add(mesh);
  },

  // One building per tick — avoids the ~8–50ms first-frame hitch from creating
  // all geometry synchronously on init. ~10 frames total, imperceptible pop-in.
  tick: function () {
    if (!this._buildQueue || this._built >= this._buildQueue.length) { return; }
    this._spawnBuilding(this._buildQueue[this._built++]);
  },

  remove: function () {
    const cityObj = this.el.getObject3D('city');
    if (cityObj) {
      cityObj.traverse(child => {
        if (child.geometry) { child.geometry.dispose(); }
        // Dispose materials that are NOT the shared building material.
        // _buildingMat is shared across all buildings — dispose it separately below.
        if (child.material && child.material !== this._buildingMat) {
          child.material.dispose();
        }
      });
      this.el.removeObject3D('city');
    }

    // Dispose shared building material after all traversal.
    if (this._buildingMat) {
      this._buildingMat.dispose();
      this._buildingMat = null;
    }

    ['hemi-light', 'light-0', 'light-1'].forEach(key => {
      if (this.el.getObject3D(key)) { this.el.removeObject3D(key); }
    });

    this._buildQueue = null;
  },
});

if (import.meta.hot) { import.meta.hot.decline(); }
